import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { FileText, User, CreditCard, LogOut, Menu, Crown, LayoutDashboard, ClipboardList, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Navbar() {
  const { user, signOut } = useAuth();
  const { isPro } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === '/invoices/new') {
      return location.pathname.startsWith('/invoices');
    }
    return location.pathname.startsWith(path);
  };

  const getLinkClass = (path: string) => {
    const active = isActive(path);
    return cn(
      "text-sm font-medium transition-all duration-200 px-3 py-1.5 rounded-full",
      active
        ? "bg-primary/10 text-primary font-semibold"
        : "text-muted-foreground hover:text-primary hover:bg-slate-100/80"
    );
  };

  return (
    <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50" aria-label="Main Navigation">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={getLinkClass('/dashboard')}
                >
                  Dashboard
                </Link>
                <Link
                  to="/quotations"
                  className={getLinkClass('/quotations')}
                >
                  Quotations
                </Link>
                <Link
                  to="/invoices/new"
                  className={getLinkClass('/invoices/new')}
                >
                  Create Invoice
                </Link>
                <Link
                  to="/blog"
                  className={getLinkClass('/blog')}
                >
                  Blog
                </Link>
                <Link
                  to="/security"
                  className={getLinkClass('/security')}
                >
                  Security
                </Link>
                {isPro ? (
                  <Link
                    to="/analytics"
                    className={getLinkClass('/analytics')}
                  >
                    Analytics
                  </Link>
                ) : (
                  <button
                    disabled
                    className="text-sm font-medium text-muted-foreground/40 cursor-not-allowed flex items-center gap-1.5 px-3 py-1.5"
                    title="Upgrade to Pro to access analytics dashboard"
                  >
                    Analytics
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold">PRO</Badge>
                  </button>
                )}
                
                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.photoURL || undefined} />
                        <AvatarFallback>
                          {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      {isPro && (
                        <Crown className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end">
                    <div className="flex items-center justify-start gap-2 p-2">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.displayName}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user.email}
                        </p>
                        {isPro && (
                          <Badge variant="default" className="w-fit mt-1">
                            <Crown className="h-3 w-3 mr-1" />
                            Pro
                          </Badge>
                        )}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/invoices')}>
                      <FileText className="mr-2 h-4 w-4" />
                      Invoices
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/quotations')}>
                      <ClipboardList className="mr-2 h-4 w-4" />
                      Quotations
                    </DropdownMenuItem>
                    {isPro ? (
                      <DropdownMenuItem onClick={() => navigate('/analytics')}>
                        <BarChart3 className="mr-2 h-4 w-4" />
                        Analytics
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem 
                        onClick={() => navigate('/subscription')}
                        className="flex items-center justify-between text-muted-foreground/70 cursor-pointer"
                      >
                        <div className="flex items-center">
                          <BarChart3 className="mr-2 h-4 w-4" />
                          <span>Analytics</span>
                        </div>
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold ml-2">PRO</Badge>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/subscription')}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Subscription
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/try-it-out">
                  <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold border-none mr-2 h-9 px-4">
                    Try It Out
                  </Button>
                </Link>
                <Link
                  to="/pricing"
                  className={getLinkClass('/pricing')}
                >
                  Pricing
                </Link>
                <Link
                  to="/security"
                  className={getLinkClass('/security')}
                >
                  Security
                </Link>
                <Link
                  to="/templates"
                  className={getLinkClass('/templates')}
                >
                  Templates
                </Link>
                <Link
                  to="/guide"
                  className={getLinkClass('/guide')}
                >
                  Guide
                </Link>
                <Link
                  to="/faq"
                  className={getLinkClass('/faq')}
                >
                  FAQ
                </Link>
                <Link
                  to="/blog"
                  className={getLinkClass('/blog')}
                >
                  Blog
                </Link>
                <Link
                  to="/about"
                  className={getLinkClass('/about')}
                >
                  About
                </Link>
                <Link to="/login">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button>Get Started Free</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Toggle navigation menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {user ? (
                  <>
                    <DropdownMenuItem 
                      onClick={() => navigate('/dashboard')}
                      className={cn("cursor-pointer", isActive('/dashboard') && "text-primary font-semibold bg-primary/5")}
                    >
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/quotations')}
                      className={cn("cursor-pointer", isActive('/quotations') && "text-primary font-semibold bg-primary/5")}
                    >
                      Quotations
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/invoices/new')}
                      className={cn("cursor-pointer", isActive('/invoices/new') && "text-primary font-semibold bg-primary/5")}
                    >
                      Create Invoice
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/blog')}
                      className={cn("cursor-pointer", isActive('/blog') && "text-primary font-semibold bg-primary/5")}
                    >
                      Blog
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/security')}
                      className={cn("cursor-pointer", isActive('/security') && "text-primary font-semibold bg-primary/5")}
                    >
                      Security
                    </DropdownMenuItem>
                    {isPro ? (
                      <DropdownMenuItem 
                        onClick={() => navigate('/analytics')}
                        className={cn("cursor-pointer", isActive('/analytics') && "text-primary font-semibold bg-primary/5")}
                      >
                        Analytics
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem 
                        disabled 
                        className="flex items-center justify-between text-muted-foreground/40 cursor-not-allowed"
                      >
                        <span>Analytics</span>
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold ml-2">PRO</Badge>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className={cn("cursor-pointer", isActive('/profile') && "text-primary font-semibold bg-primary/5")}
                    >
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/subscription')}
                      className={cn("cursor-pointer", isActive('/subscription') && "text-primary font-semibold bg-primary/5")}
                    >
                      Subscription
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      Sign Out
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/try-it-out')} className="text-emerald-600 font-bold focus:text-emerald-700 bg-emerald-50/30 focus:bg-emerald-50">
                      Try It Out (No Login)
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/pricing')}
                      className={cn("cursor-pointer", isActive('/pricing') && "text-primary font-semibold bg-primary/5")}
                    >
                      Pricing
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/guide')}
                      className={cn("cursor-pointer", isActive('/guide') && "text-primary font-semibold bg-primary/5")}
                    >
                      Guide
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/faq')}
                      className={cn("cursor-pointer", isActive('/faq') && "text-primary font-semibold bg-primary/5")}
                    >
                      FAQ
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/blog')}
                      className={cn("cursor-pointer", isActive('/blog') && "text-primary font-semibold bg-primary/5")}
                    >
                      Blog
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/about')}
                      className={cn("cursor-pointer", isActive('/about') && "text-primary font-semibold bg-primary/5")}
                    >
                      About
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/security')}
                      className={cn("cursor-pointer", isActive('/security') && "text-primary font-semibold bg-primary/5")}
                    >
                      Security
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/login')}>
                      Sign In
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/register')}>
                      Get Started Free
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}
